import {
  CallDto,
  CallConnectionDto,
  SuggestedActionDto,
  SuggestedNeedDto,
  SuggestedScheduledEventDto,
} from '@act/shared/data-transfer-objects';
import { PayloadAction } from '@reduxjs/toolkit';
import {
  CallWSPayload,
  GuidesDomainAnalysisReadyPayload,
} from '@act/shared/socket-events';
import { CallState } from '../types/callsTypes';
import {
  callsAdapter,
  mergeCallUpdatePreservingRecording,
} from './callsSliceState.util';

/**
 * Synchronous reducers for the calls slice.
 */
export const callsSliceReducers = {
  removeCallFromState: (state: CallState, action: { payload: number }) => {
    callsAdapter.removeOne(state.calls, action.payload);
  },
  callUpdated: (
    state: CallState,
    action: PayloadAction<{ call: CallDto; originalCall: CallDto }>
  ) => {
    const { call } = action.payload;
    const existing = state.calls.entities[call.id];
    if (existing) {
      state.calls.entities[call.id] = mergeCallUpdatePreservingRecording(
        existing,
        call
      );
    }
  },
  callConnectionUpdated: (
    state: CallState,
    action: PayloadAction<{ connection: Partial<CallConnectionDto> & { callId?: number } }>
  ) => {
    const { connection } = action.payload;
    const callId = connection.callId;
    if (callId && state.calls.entities[callId]) {
      const call = state.calls.entities[callId];
      if (call.connections) {
        const connectionIndex = call.connections.findIndex(
          c => c.id === connection.id
        );
        if (connectionIndex >= 0) {
          call.connections[connectionIndex] = {
            ...call.connections[connectionIndex],
            ...connection,
          } as CallConnectionDto;
        } else if (
          connection.id != null &&
          connection.userId != null &&
          connection.sid != null &&
          connection.phoneNumber != null &&
          connection.status != null
        ) {
          call.connections.push({
            id: connection.id,
            callId,
            userId: connection.userId,
            sid: connection.sid,
            phoneNumber: connection.phoneNumber,
            status: connection.status,
            onHold: connection.onHold ?? false,
            connectedAt: connection.connectedAt,
            completedAt: connection.completedAt,
            isAdded: connection.isAdded ?? false,
          });
        }
      } else if (
        connection.id != null &&
        connection.userId != null &&
        connection.sid != null &&
        connection.phoneNumber != null &&
        connection.status != null
      ) {
        call.connections = [
          {
            id: connection.id,
            callId,
            userId: connection.userId,
            sid: connection.sid,
            phoneNumber: connection.phoneNumber,
            status: connection.status,
            onHold: connection.onHold ?? false,
            connectedAt: connection.connectedAt,
            completedAt: connection.completedAt,
            isAdded: connection.isAdded ?? false,
          },
        ];
      }
      if (call.originator?.userId === connection.userId) {
        call.originator.onHold = connection.onHold ?? false;
      }
      if (call.receiver?.userId === connection.userId) {
        call.receiver.onHold = connection.onHold ?? false;
      }
    }
    if (callId && state.justInTimeCalls[callId]) {
      const justInTimeCall = state.justInTimeCalls[callId];

      if (justInTimeCall.timeEnded) {
        const { [callId]: removed, ...remaining } = state.justInTimeCalls;
        state.justInTimeCalls = remaining;
      } else if (justInTimeCall.connections) {
        const connectionIndex = justInTimeCall.connections.findIndex(
          c => c.id === connection.id
        );
        if (connectionIndex >= 0) {
          justInTimeCall.connections[connectionIndex] = {
            ...justInTimeCall.connections[connectionIndex],
            ...connection,
          } as CallConnectionDto;
        } else if (
          connection.id != null &&
          connection.userId != null &&
          connection.sid != null &&
          connection.phoneNumber != null &&
          connection.status != null
        ) {
          justInTimeCall.connections.push({
            id: connection.id,
            callId,
            userId: connection.userId,
            sid: connection.sid,
            phoneNumber: connection.phoneNumber,
            status: connection.status,
            onHold: connection.onHold ?? false,
            connectedAt: connection.connectedAt,
            completedAt: connection.completedAt,
            isAdded: connection.isAdded ?? false,
          });
        }
      } else if (
        connection.id != null &&
        connection.userId != null &&
        connection.sid != null &&
        connection.phoneNumber != null &&
        connection.status != null
      ) {
        justInTimeCall.connections = [
          {
            id: connection.id,
            callId,
            userId: connection.userId,
            sid: connection.sid,
            phoneNumber: connection.phoneNumber,
            status: connection.status,
            onHold: connection.onHold ?? false,
            connectedAt: connection.connectedAt,
            completedAt: connection.completedAt,
            isAdded: connection.isAdded ?? false,
          },
        ];
      }
    }
  },
  callCreated: (state: CallState, action: PayloadAction<CallWSPayload>) => {
    const { call } = action.payload;
    state.justInTimeCalls[call.id] = call;
    if (!state.calls.entities[call.id]) {
      callsAdapter.addOne(state.calls, call);
    }
  },
  guidesDomainAnalysisReady: (
    state: CallState,
    action: PayloadAction<GuidesDomainAnalysisReadyPayload>
  ) => {
    const { callId, guidesDomainAnalysis } = action.payload;
    const existing = state.calls.entities[callId];

    if (existing?.recording) {
      existing.recording.guidesDomainAnalysis = guidesDomainAnalysis;
    }

    state.recapReadyNotice = { callId };
  },
  clearRecapReadyNotice: (state: CallState) => {
    state.recapReadyNotice = null;
  },
  justInTimeCallUpdated: (
    state: CallState,
    action: PayloadAction<CallWSPayload>
  ) => {
    const { call } = action.payload;

    if (call.timeEnded && state.justInTimeCalls[call.id]) {
      const { [call.id]: removed, ...remaining } = state.justInTimeCalls;
      state.justInTimeCalls = remaining;
    } else if (state.justInTimeCalls[call.id]) {
      state.justInTimeCalls[call.id] = mergeCallUpdatePreservingRecording(
        state.justInTimeCalls[call.id],
        call
      );
    }

    const existingEntity = state.calls.entities[call.id];
    if (existingEntity) {
      state.calls.entities[call.id] = mergeCallUpdatePreservingRecording(
        existingEntity,
        call
      );
    }
  },
  removeJustInTimeCall: (state: CallState, action: PayloadAction<number>) => {
    const { [action.payload]: removed, ...remaining } = state.justInTimeCalls;
    state.justInTimeCalls = remaining;

    return state;
  },
  suggestedActionsUpdated: (
    state: CallState,
    action: PayloadAction<{
      call: CallDto;
      isLlmSuggestionsProcessed?: boolean;
      llmSuggestions?: {
        needs?: SuggestedNeedDto[];
        actions?: SuggestedActionDto[];
        scheduledEvents?: SuggestedScheduledEventDto[];
      };
    }>
  ) => {
    const callId = action.payload.call.id;
    const { llmSuggestions } = action.payload;
    const existingCall = state.calls.entities[callId];
    if (existingCall) {
      state.calls.entities[callId] = mergeCallUpdatePreservingRecording(
        existingCall,
        action.payload.call
      );
    }
    if (llmSuggestions) {
      const existing = state.suggestions[callId] ?? {
        needs: [],
        actions: [],
        scheduledEvents: [],
      };

      const mergeById = <T extends { id?: string }>(
        existingList: T[],
        incomingList: T[] | undefined | null
      ): T[] => {
        if (!incomingList?.length) return existingList;
        const result = existingList.filter(
          (item): item is T => item != null
        ) as T[];
        for (const incoming of incomingList) {
          if (incoming == null) continue;
          const matchIndex = incoming.id
            ? result.findIndex(item => item?.id === incoming.id)
            : -1;
          if (matchIndex >= 0) {
            result[matchIndex] = incoming;
          } else {
            result.push(incoming);
          }
        }
        return result;
      };

      const mergedActions = mergeById(
        existing.actions,
        llmSuggestions?.actions
      ) as SuggestedActionDto[];
      const actionsWithPreservedNeed = mergedActions.map(
        (suggestedAction: SuggestedActionDto) => {
          const existingAction = existing.actions.find(
            a => a?.id != null && a.id === suggestedAction?.id
          );
          if (!!existingAction?.need && !suggestedAction.need) {
            return { ...suggestedAction, need: existingAction.need };
          }
          return suggestedAction;
        }
      );

      state.suggestions[callId] = {
        needs: mergeById(existing.needs, llmSuggestions?.needs),
        actions: actionsWithPreservedNeed.filter(
          (a: SuggestedActionDto) => !(a?.tempNeedId && !a?.needId)
        ),
        scheduledEvents: mergeById(
          existing.scheduledEvents,
          llmSuggestions?.scheduledEvents
        ),
      };
    }
  },
  setMinimizedCallId: (
    state: CallState,
    action: PayloadAction<number | null>
  ) => {
    state.minimizedCallId = action.payload;
  },
};
